// src/components/AdminComponents/sections/contractdetailsview.js
import React, { useState, useEffect } from 'react';
import {
  Box, Typography, Button, Card, CardContent, CardActions,
  Dialog, DialogTitle, DialogContent, DialogActions, TextField,
  FormControl, InputLabel, Select, MenuItem, Chip, Grid,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Paper, Tabs, Tab, IconButton, Alert, CircularProgress,
  Accordion, AccordionSummary, AccordionDetails, Switch, FormControlLabel,
  LinearProgress, Badge, List, ListItem, ListItemText, ListItemIcon,
  Divider, Tooltip
} from '@mui/material';
import {
  Add as AddIcon, Edit as EditIcon, Delete as DeleteIcon,
  Send as SendIcon, Visibility as ViewIcon, Assignment as AssignIcon,
  CheckCircle as SignedIcon, Pending as PendingIcon, Cancel as DeclinedIcon,
  NotificationsActive as RemindIcon, ExpandMore as ExpandMoreIcon,
  Business as CompanyIcon, Group as TeamIcon, Person as UserIcon,
  Analytics as AnalyticsIcon, History as HistoryIcon,
  Description as ContractIcon, Dashboard as DashboardIcon
} from '@mui/icons-material';
import client from "../../../../API"; // Adjust to your API client location

// Main Contract Management View for Admin Section
const ContractDetailsView = () => {
  const [activeTab, setActiveTab] = useState(0);
  const [systemStats, setSystemStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchOverviewData();
  }, []);

  const fetchOverviewData = async () => {
    try {
      setLoading(true);
      const stats = await client.contracts.getSystemStats();
      setSystemStats(stats.stats);
    } catch (error) {
      console.error('Failed to fetch contract overview:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  const tabs = [
    { label: 'Overview', icon: <DashboardIcon /> },
    { label: 'Templates', icon: <ContractIcon /> },
    { label: 'Assignments', icon: <SendIcon /> },
    { label: 'Analytics', icon: <AnalyticsIcon /> },
    { label: 'Pending Signatures', icon: <PendingIcon /> }
  ];

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom>
        Contract Management
      </Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
        Manage contract templates, assignments, and monitor signature progress
      </Typography>

      {/* Quick Stats Cards */}
      {systemStats && (
        <Grid container spacing={3} sx={{ mb: 4 }}>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Typography color="textSecondary" gutterBottom variant="body2">
                  Total Templates
                </Typography>
                <Typography variant="h4">
                  {systemStats.total_templates}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Typography color="textSecondary" gutterBottom variant="body2">
                  Active Templates
                </Typography>
                <Typography variant="h4" color="success.main">
                  {systemStats.active_templates}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Typography color="textSecondary" gutterBottom variant="body2">
                  Total Assignments
                </Typography>
                <Typography variant="h4">
                  {systemStats.total_assignments}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Typography color="textSecondary" gutterBottom variant="body2">
                  Completion Rate
                </Typography>
                <Typography variant="h4" color="primary.main">
                  {systemStats.completion_rate?.toFixed(1)}%
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* Tabs */}
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
        <Tabs value={activeTab} onChange={handleTabChange}>
          {tabs.map((tab, index) => (
            <Tab
              key={index}
              label={tab.label}
              icon={tab.icon}
              iconPosition="start"
            />
          ))}
        </Tabs>
      </Box>

      {/* Tab Content */}
      {activeTab === 0 && <ContractOverview />}
      {activeTab === 1 && <ContractTemplatesAdmin />}
      {activeTab === 2 && <ContractAssignmentsAdmin />}
      {activeTab === 3 && <ContractAnalyticsAdmin />}
      {activeTab === 4 && <PendingSignaturesAdmin />}
    </Box>
  );
};

// Overview Tab Component
const ContractOverview = () => {
  const [recentActivity, setRecentActivity] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchOverviewData();
  }, []);

  const fetchOverviewData = async () => {
    try {
      const auditLogs = await client.contracts.getAuditLogs({ page: 1, per_page: 10 });
      setRecentActivity(auditLogs.audit_logs || []);
    } catch (error) {
      console.error('Failed to fetch overview data:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" p={4}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Grid container spacing={3}>
      <Grid item xs={12} md={8}>
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Recent Contract Activity
            </Typography>
            <List>
              {recentActivity.slice(0, 10).map((activity) => (
                <ListItem key={activity.id}>
                  <ListItemIcon>
                    {activity.action.includes('signed') ? (
                      <SignedIcon color="success" />
                    ) : activity.action.includes('declined') ? (
                      <DeclinedIcon color="error" />
                    ) : activity.action.includes('assigned') ? (
                      <SendIcon color="primary" />
                    ) : (
                      <ContractIcon />
                    )}
                  </ListItemIcon>
                  <ListItemText
                    primary={activity.action.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())}
                    secondary={`${new Date(activity.created_at).toLocaleString()}`}
                  />
                </ListItem>
              ))}
            </List>
          </CardContent>
        </Card>
      </Grid>
      
      <Grid item xs={12} md={4}>
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Quick Actions
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                fullWidth
              >
                Create Template
              </Button>
              <Button
                variant="outlined"
                startIcon={<SendIcon />}
                fullWidth
              >
                Assign Contract
              </Button>
              <Button
                variant="outlined"
                startIcon={<AnalyticsIcon />}
                fullWidth
              >
                View Analytics
              </Button>
            </Box>
          </CardContent>
        </Card>
      </Grid>
    </Grid>
  );
};

// Contract Templates Admin Component
const ContractTemplatesAdmin = () => {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [editTemplate, setEditTemplate] = useState(null);

  useEffect(() => {
    fetchTemplates();
  }, []);

  const fetchTemplates = async () => {
    try {
      const data = await client.contracts.getTemplates();
      setTemplates(data.templates || []);
    } catch (error) {
      console.error('Failed to fetch templates:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteTemplate = async (templateId) => {
    if (!window.confirm('Are you sure you want to delete this template?')) return;
    
    try {
      await client.contracts.deleteTemplate(templateId);
      await fetchTemplates();
    } catch (error) {
      console.error('Failed to delete template:', error);
      alert('Failed to delete template. It may have active assignments.');
    }
  };

  const toggleTemplateStatus = async (template) => {
    try {
      const newStatus = template.status === 'active' ? 'archived' : 'active';
      await client.contracts.updateTemplate(template.id, { status: newStatus });
      await fetchTemplates();
    } catch (error) {
      console.error('Failed to update template status:', error);
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" p={4}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h6">
          Contract Templates ({templates.length})
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setCreateDialogOpen(true)}
        >
          Create Template
        </Button>
      </Box>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Name</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Version</TableCell>
              <TableCell>Assignments</TableCell>
              <TableCell>Created</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {templates.map((template) => (
              <TableRow key={template.id}>
                <TableCell>
                  <Box>
                    <Typography variant="body1" fontWeight="medium">
                      {template.name}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {template.description}
                    </Typography>
                  </Box>
                </TableCell>
                <TableCell>
                  <Chip
                    label={template.status}
                    color={template.status === 'active' ? 'success' : 'default'}
                    size="small"
                  />
                </TableCell>
                <TableCell>v{template.version}</TableCell>
                <TableCell>
                  <Badge badgeContent={template.assignment_count} color="primary">
                    <AssignIcon />
                  </Badge>
                </TableCell>
                <TableCell>
                  {new Date(template.created_at).toLocaleDateString()}
                </TableCell>
                <TableCell>
                  <Box display="flex" gap={1}>
                    <Tooltip title="Edit">
                      <IconButton
                        size="small"
                        onClick={() => setEditTemplate(template)}
                      >
                        <EditIcon />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title={template.status === 'active' ? 'Archive' : 'Activate'}>
                      <IconButton
                        size="small"
                        onClick={() => toggleTemplateStatus(template)}
                      >
                        {template.status === 'active' ? '📦' : '✅'}
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete">
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => handleDeleteTemplate(template.id)}
                        disabled={template.assignment_count > 0}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Create/Edit Template Dialog */}
      <ContractTemplateDialog
        open={createDialogOpen || !!editTemplate}
        template={editTemplate}
        onClose={() => {
          setCreateDialogOpen(false);
          setEditTemplate(null);
        }}
        onSave={fetchTemplates}
      />
    </Box>
  );
};

// Contract Template Dialog
const ContractTemplateDialog = ({ open, template, onClose, onSave }) => {
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    js_content: '',
    expires_after_days: '',
    requires_resignation_on_update: true
  });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (template) {
      setFormData({
        name: template.name || '',
        description: template.description || '',
        js_content: template.contract_body || '',
        expires_after_days: template.expires_after_days || '',
        requires_resignation_on_update: template.requires_resignation_on_update ?? true
      });
    } else {
      setFormData({
        name: '',
        description: '',
        js_content: '',
        expires_after_days: '',
        requires_resignation_on_update: true
      });
    }
  }, [template]);

  const handleSubmit = async () => {
    if (!formData.name.trim() || !formData.js_content.trim()) {
      alert('Name and contract content are required');
      return;
    }

    try {
      setLoading(true);
      if (template) {
        await client.contracts.updateTemplate(template.id, formData);
      } else {
        await client.contracts.createTemplate(formData);
      }
      onSave();
      onClose();
    } catch (error) {
      console.error('Failed to save template:', error);
      alert('Failed to save template');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        {template ? 'Edit Template' : 'Create Contract Template'}
      </DialogTitle>
      <DialogContent>
        <Grid container spacing={2} sx={{ mt: 1 }}>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Template Name"
              value={formData.name}
              onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Description"
              multiline
              rows={2}
              value={formData.description}
              onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Expires After (Days)"
              type="number"
              value={formData.expires_after_days}
              onChange={(e) => setFormData(prev => ({ ...prev, expires_after_days: e.target.value }))}
              helperText="Leave empty for no expiration"
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <FormControlLabel
              control={
                <Switch
                  checked={formData.requires_resignation_on_update}
                  onChange={(e) => setFormData(prev => ({ ...prev, requires_resignation_on_update: e.target.checked }))}
                />
              }
              label="Require re-signing on updates"
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Contract Content (HTML/React)"
              multiline
              rows={10}
              value={formData.js_content}
              onChange={(e) => setFormData(prev => ({ ...prev, js_content: e.target.value }))}
              helperText="Paste your React component or HTML content here"
            />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={loading}
        >
          {loading ? <CircularProgress size={20} /> : (template ? 'Update' : 'Create')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// Contract Assignments Admin Component
const ContractAssignmentsAdmin = () => {
  const [assignments, setAssignments] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [assignDialogOpen, setAssignDialogOpen] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [assignmentsData, templatesData] = await Promise.all([
        client.contracts.getAssignments(),
        client.contracts.getTemplates()
      ]);
      setAssignments(assignmentsData.assignments || []);
      setTemplates(templatesData.templates || []);
    } catch (error) {
      console.error('Failed to fetch data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSendReminders = async (assignmentId) => {
    try {
      const result = await client.contracts.sendReminders(assignmentId);
      alert(`Sent ${result.reminder_count} reminders successfully`);
    } catch (error) {
      console.error('Failed to send reminders:', error);
      alert('Failed to send reminders');
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" p={4}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h6">
          Contract Assignments ({assignments.length})
        </Typography>
        <Button
          variant="contained"
          startIcon={<AssignIcon />}
          onClick={() => setAssignDialogOpen(true)}
        >
          Assign Contract
        </Button>
      </Box>

      {assignments.map((assignment) => (
        <Accordion key={assignment.id}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Box display="flex" alignItems="center" width="100%">
              <Box flex={1}>
                <Typography variant="subtitle1" fontWeight="medium">
                  {assignment.template?.name}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {assignment.assignment_scope} • {assignment.signature_stats?.total} signatures
                </Typography>
              </Box>
              <Box display="flex" gap={1} alignItems="center">
                <Chip
                  icon={assignment.assignment_scope === 'company' ? <CompanyIcon /> : 
                        assignment.assignment_scope === 'team' ? <TeamIcon /> : <UserIcon />}
                  label={assignment.assignment_scope}
                  size="small"
                  variant="outlined"
                />
                <Chip
                  label={`${assignment.signature_stats?.signed}/${assignment.signature_stats?.total}`}
                  color={assignment.signature_stats?.completion_rate === 100 ? 'success' : 'info'}
                  size="small"
                />
              </Box>
            </Box>
          </AccordionSummary>
          <AccordionDetails>
            <Grid container spacing={3}>
              <Grid item xs={12} md={8}>
                <Typography variant="body2" gutterBottom>
                  <strong>Message:</strong> {assignment.assignment_message || 'No message'}
                </Typography>
                {assignment.due_date && (
                  <Typography variant="body2" gutterBottom>
                    <strong>Due Date:</strong> {new Date(assignment.due_date).toLocaleDateString()}
                  </Typography>
                )}
                
                {/* Progress Bar */}
                <Box sx={{ mt: 2, mb: 2 }}>
                  <Box display="flex" justifyContent="space-between" mb={1}>
                    <Typography variant="body2">
                      Completion Progress
                    </Typography>
                    <Typography variant="body2">
                      {assignment.signature_stats?.completion_rate?.toFixed(1)}%
                    </Typography>
                  </Box>
                  <LinearProgress
                    variant="determinate"
                    value={assignment.signature_stats?.completion_rate || 0}
                    sx={{ height: 8, borderRadius: 4 }}
                  />
                </Box>

                {/* Statistics */}
                <Box display="flex" gap={1} flexWrap="wrap">
                  <Chip
                    icon={<SignedIcon />}
                    label={`${assignment.signature_stats?.signed} Signed`}
                    color="success"
                    size="small"
                  />
                  <Chip
                    icon={<PendingIcon />}
                    label={`${assignment.signature_stats?.pending} Pending`}
                    color="warning"
                    size="small"
                  />
                  <Chip
                    icon={<DeclinedIcon />}
                    label={`${assignment.signature_stats?.declined} Declined`}
                    color="error"
                    size="small"
                  />
                </Box>
              </Grid>
              <Grid item xs={12} md={4}>
                <Box display="flex" flexDirection="column" gap={1}>
                  <Button
                    variant="outlined"
                    startIcon={<RemindIcon />}
                    onClick={() => handleSendReminders(assignment.id)}
                    disabled={assignment.signature_stats?.pending === 0}
                    size="small"
                  >
                    Send Reminders ({assignment.signature_stats?.pending})
                  </Button>
                  <Button
                    variant="outlined"
                    startIcon={<ViewIcon />}
                    size="small"
                  >
                    View Details
                  </Button>
                </Box>
              </Grid>
            </Grid>
          </AccordionDetails>
        </Accordion>
      ))}

      {/* Contract Assignment Dialog */}
      <ContractAssignmentDialog
        open={assignDialogOpen}
        templates={templates}
        onClose={() => setAssignDialogOpen(false)}
        onAssign={fetchData}
      />
    </Box>
  );
};

// Contract Assignment Dialog
const ContractAssignmentDialog = ({ open, templates, onClose, onAssign }) => {
  const [formData, setFormData] = useState({
    template_id: '',
    scope: 'company',
    company_id: '',
    team_id: '',
    user_id: '',
    due_date: '',
    message: '',
    is_mandatory: true,
    auto_remind: true,
    reminder_frequency_days: 7
  });
  const [companies, setCompanies] = useState([]);
  const [teams, setTeams] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (open) {
      fetchOptions();
    }
  }, [open]);

  useEffect(() => {
    if (formData.scope === 'team' && formData.company_id) {
      fetchTeams(formData.company_id);
    }
    if (formData.scope === 'user' && formData.company_id) {
      fetchUsers(formData.company_id);
    }
  }, [formData.scope, formData.company_id]);

  const fetchOptions = async () => {
    try {
      const companiesData = await client.companies.list();
      setCompanies(companiesData.companies || []);
    } catch (error) {
      console.error('Failed to fetch companies:', error);
    }
  };

  const fetchTeams = async (companyId) => {
    try {
      const teamsData = await client.companies.teams.list(companyId);
      setTeams(teamsData.teams || []);
    } catch (error) {
      console.error('Failed to fetch teams:', error);
    }
  };

  const fetchUsers = async (companyId) => {
    try {
      const usersData = await client.companies.getUsers(companyId);
      setUsers(usersData.users || []);
    } catch (error) {
      console.error('Failed to fetch users:', error);
    }
  };

  const handleSubmit = async () => {
    if (!formData.template_id || !formData.scope) {
      alert('Template and scope are required');
      return;
    }

    try {
      setLoading(true);
      await client.contracts.assignContract(formData);
      onAssign();
      onClose();
      // Reset form
      setFormData({
        template_id: '',
        scope: 'company',
        company_id: '',
        team_id: '',
        user_id: '',
        due_date: '',
        message: '',
        is_mandatory: true,
        auto_remind: true,
        reminder_frequency_days: 7
      });
    } catch (error) {
      console.error('Failed to assign contract:', error);
      alert('Failed to assign contract');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Assign Contract</DialogTitle>
      <DialogContent>
        <Grid container spacing={2} sx={{ mt: 1 }}>
          <Grid item xs={12}>
            <FormControl fullWidth>
              <InputLabel>Contract Template</InputLabel>
              <Select
                value={formData.template_id}
                onChange={(e) => setFormData(prev => ({ ...prev, template_id: e.target.value }))}
              >
                {templates.filter(t => t.status === 'active').map((template) => (
                  <MenuItem key={template.id} value={template.id}>
                    {template.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          
          <Grid item xs={12} md={6}>
            <FormControl fullWidth>
              <InputLabel>Assignment Scope</InputLabel>
              <Select
                value={formData.scope}
                onChange={(e) => setFormData(prev => ({ 
                  ...prev, 
                  scope: e.target.value,
                  team_id: '',
                  user_id: ''
                }))}
              >
                <MenuItem value="company">Entire Company</MenuItem>
                <MenuItem value="team">Specific Team</MenuItem>
                <MenuItem value="user">Individual User</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} md={6}>
            <FormControl fullWidth>
              <InputLabel>Company</InputLabel>
              <Select
                value={formData.company_id}
                onChange={(e) => setFormData(prev => ({ 
                  ...prev, 
                  company_id: e.target.value,
                  team_id: '',
                  user_id: ''
                }))}
              >
                {companies.map((company) => (
                  <MenuItem key={company.id} value={company.id}>
                    {company.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          {formData.scope === 'team' && (
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Team</InputLabel>
                <Select
                  value={formData.team_id}
                  onChange={(e) => setFormData(prev => ({ ...prev, team_id: e.target.value }))}
                >
                  {teams.map((team) => (
                    <MenuItem key={team.id} value={team.id}>
                      {team.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          )}

          {formData.scope === 'user' && (
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>User</InputLabel>
                <Select
                  value={formData.user_id}
                  onChange={(e) => setFormData(prev => ({ ...prev, user_id: e.target.value }))}
                >
                  {users.map((user) => (
                    <MenuItem key={user.id} value={user.id}>
                      {user.first_name} {user.last_name} ({user.email})
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          )}

          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Due Date"
              type="datetime-local"
              value={formData.due_date}
              onChange={(e) => setFormData(prev => ({ ...prev, due_date: e.target.value }))}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Reminder Frequency (Days)"
              type="number"
              value={formData.reminder_frequency_days}
              onChange={(e) => setFormData(prev => ({ ...prev, reminder_frequency_days: e.target.value }))}
            />
          </Grid>

          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Assignment Message"
              multiline
              rows={3}
              value={formData.message}
              onChange={(e) => setFormData(prev => ({ ...prev, message: e.target.value }))}
              helperText="Optional message to include with the contract assignment"
            />
          </Grid>

          <Grid item xs={12}>
            <Box display="flex" gap={2}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.is_mandatory}
                    onChange={(e) => setFormData(prev => ({ ...prev, is_mandatory: e.target.checked }))}
                  />
                }
                label="Mandatory Signature"
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.auto_remind}
                    onChange={(e) => setFormData(prev => ({ ...prev, auto_remind: e.target.checked }))}
                  />
                }
                label="Automatic Reminders"
              />
            </Box>
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={loading}
        >
          {loading ? <CircularProgress size={20} /> : 'Assign Contract'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// Contract Analytics Component
const ContractAnalyticsAdmin = () => {
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchAnalytics();
  }, []);

  const fetchAnalytics = async () => {
    try {
      const systemStats = await client.contracts.getSystemStats();
      setAnalytics(systemStats.stats);
    } catch (error) {
      console.error('Failed to fetch analytics:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" p={4}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Grid container spacing={3}>
      <Grid item xs={12} md={6}>
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              System-wide Statistics
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <Typography variant="body2" color="text.secondary">Total Templates</Typography>
                <Typography variant="h4">{analytics?.total_templates}</Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="body2" color="text.secondary">Active Templates</Typography>
                <Typography variant="h4" color="success.main">{analytics?.active_templates}</Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="body2" color="text.secondary">Total Assignments</Typography>
                <Typography variant="h4">{analytics?.total_assignments}</Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="body2" color="text.secondary">Overall Completion Rate</Typography>
                <Typography variant="h4" color="primary.main">
                  {analytics?.completion_rate?.toFixed(1)}%
                </Typography>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      </Grid>

      <Grid item xs={12} md={6}>
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Signature Status Breakdown
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={4}>
                <Box textAlign="center">
                  <SignedIcon color="success" fontSize="large" />
                  <Typography variant="h5" color="success.main">
                    {analytics?.signed_contracts}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Signed
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={4}>
                <Box textAlign="center">
                  <PendingIcon color="warning" fontSize="large" />
                  <Typography variant="h5" color="warning.main">
                    {analytics?.pending_contracts}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Pending
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={4}>
                <Box textAlign="center">
                  <DeclinedIcon color="error" fontSize="large" />
                  <Typography variant="h5" color="error.main">
                    {analytics?.total_signatures - analytics?.signed_contracts - analytics?.pending_contracts}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Declined/Expired
                  </Typography>
                </Box>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      </Grid>
    </Grid>
  );
};

// Pending Signatures Component
const PendingSignaturesAdmin = () => {
  const [pendingSignatures, setPendingSignatures] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchPendingSignatures();
  }, []);

  const fetchPendingSignatures = async () => {
    try {
      const assignments = await client.contracts.getAssignments();
      
      // Filter to show only assignments with pending signatures
      const pendingAssignments = assignments.assignments?.filter(
        assignment => assignment.signature_stats?.pending > 0
      ) || [];
      
      setPendingSignatures(pendingAssignments);
    } catch (error) {
      console.error('Failed to fetch pending signatures:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" p={4}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        Assignments with Pending Signatures ({pendingSignatures.length})
      </Typography>
      
      {pendingSignatures.map((assignment) => (
        <Card key={assignment.id} sx={{ mb: 2 }}>
          <CardContent>
            <Box display="flex" justifyContent="space-between" alignItems="start">
              <Box>
                <Typography variant="h6">
                  {assignment.template?.name}
                </Typography>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  {assignment.assignment_scope} assignment
                </Typography>
                <Typography variant="body2">
                  {assignment.signature_stats?.pending} pending signatures out of {assignment.signature_stats?.total}
                </Typography>
                {assignment.due_date && (
                  <Typography variant="body2" color="warning.main">
                    Due: {new Date(assignment.due_date).toLocaleDateString()}
                  </Typography>
                )}
              </Box>
              <Box display="flex" flexDirection="column" gap={1} alignItems="end">
                <Chip
                  label={`${assignment.signature_stats?.pending} pending`}
                  color="warning"
                  size="small"
                />
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<RemindIcon />}
                >
                  Send Reminders
                </Button>
              </Box>
            </Box>
            
            <Box sx={{ mt: 2 }}>
              <LinearProgress
                variant="determinate"
                value={assignment.signature_stats?.completion_rate || 0}
                sx={{ height: 6, borderRadius: 3 }}
              />
              <Typography variant="caption" color="text.secondary">
                {assignment.signature_stats?.completion_rate?.toFixed(1)}% complete
              </Typography>
            </Box>
          </CardContent>
        </Card>
      ))}
      
      {pendingSignatures.length === 0 && (
        <Card>
          <CardContent>
            <Box textAlign="center" py={4}>
              <SignedIcon color="success" sx={{ fontSize: 64, mb: 2 }} />
              <Typography variant="h6" gutterBottom>
                All contracts are up to date!
              </Typography>
              <Typography variant="body2" color="text.secondary">
                No pending signatures require attention.
              </Typography>
            </Box>
          </CardContent>
        </Card>
      )}
    </Box>
  );
};

export default ContractDetailsView;